import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { CheckIcon, CloseIcon } from '@/components/ui/icons'
import { ToastContext, type ToastOptions } from '@/components/ui/Toast/toastContext'
import { cn } from '@/lib/utils/cn'

import styles from './Toast.module.css'

interface ActiveToast extends ToastOptions {
  id: number
  leaving: boolean
}

/** How long a toast stays put, and how long its fade takes. */
const VISIBLE_MS = 2800
const FADE_MS = 400

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([])
  const nextId = useRef(0)
  const timers = useRef<number[]>([])
  const viewportRef = useRef<HTMLDivElement>(null)

  const showToast = useCallback((options: ToastOptions) => {
    const id = nextId.current++
    setToasts((current) => [...current, { ...options, id, leaving: false }])

    timers.current.push(
      window.setTimeout(() => {
        setToasts((current) =>
          current.map((toast) => (toast.id === id ? { ...toast, leaving: true } : toast)),
        )
      }, VISIBLE_MS),
      window.setTimeout(() => {
        setToasts((current) => current.filter((toast) => toast.id !== id))
      }, VISIBLE_MS + FADE_MS),
    )
  }, [])

  useEffect(() => {
    const pending = timers.current
    return () => pending.forEach(window.clearTimeout)
  }, [])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    viewport.setAttribute('popover', 'manual')

    const raise = () => {
      if (viewport.matches(':popover-open')) viewport.hidePopover()
      viewport.showPopover()
    }

    raise()

    const observer = new MutationObserver((records) => {
      if (records.some((r) => r.target instanceof HTMLDialogElement && r.target.open)) raise()
    })
    observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] })

    return () => {
      observer.disconnect()
      if (viewport.matches(':popover-open')) viewport.hidePopover()
    }
  }, [])

  const value = useMemo(() => ({ showToast }), [showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}

      <div ref={viewportRef} className={styles.viewport} role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              styles.toast,
              toast.tone === 'error' && styles.error,
              toast.leaving && styles.leaving,
            )}
          >
            {toast.tone === 'error' ? (
              <CloseIcon className={styles.icon} />
            ) : (
              <CheckIcon className={styles.icon} />
            )}
            <span className={styles.copy}>
              <strong className={styles.title}>{toast.title}</strong>
              {toast.description && <span className={styles.description}>{toast.description}</span>}
            </span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
